/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Input } from 'app/components/ui/input';
import { MultiSelectFilter } from 'app/components/ui/multi-select-filter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'app/components/ui/tabs';
import { DataTable, DataTableGroup } from 'app/components/ui/data-table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { InputField } from 'app/components/atoms/forms/fields/input/input-field';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useSetBreadcrumbTrail } from 'app/components/templates/dashboard/app-shell/breadcrumb-provider';
import { appRoutes } from 'app/routes';
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Check,
  Copy,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Upload
} from 'lucide-react';
import moment from 'moment';
import {
  CgEntry,
  CgHolding,
  CgOnchainTx,
  CgTransaction,
  CgWallet,
  useCreateEntryFromOnchainTx,
  useCryptoGestEntries,
  useCryptoGestOnchainTx,
  useCryptoGestWallet,
  useDeleteCryptoGestEntry,
  useDeleteCryptoGestWallet,
  useCryptoGestPriceStream,
  useEditCryptoGestWallet,
  useImportNexoCsv,
  useSyncCryptoGestWallet
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { EditTransactionDialog } from '../transactions/crypto-gest-transactions';
import { ENTRY_TYPE_ORDER, amount, shortAddress } from '../format';
import { useDisplayMoney } from '../use-display-money';
import { useQueryState } from 'app/hooks/use-query-state';
import { CopyId } from 'app/components/atoms/copy-id/copy-id';
import { AssetIcon } from '../asset-icon';

/**
 * A previously imported entry the latest Nexo export no longer contains.
 */

type NexoOrphan = {
  id: string;
  symbol: string;
  type: string;
  amount: string;
  unitPriceFiat: string | null;
  timestamp: string;
  note: string | null;
};

/**
 * Export `CryptoGestWalletDetail` component.
 *
 * Wallet configuration and current state (identity, sync, holdings). The
 * wallet's transactions live in the Transactions section.
 */

export function CryptoGestWalletDetail(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { id } = useParams();
  const { data: wallet, isLoading } = useCryptoGestWallet(id);
  const { data: entries } = useCryptoGestEntries(id);
  const { data: onchainTx } = useCryptoGestOnchainTx(id);
  const createFromTx = useCreateEntryFromOnchainTx();
  const { format: usd, usdPerEur } = useDisplayMoney();

  // An entry's unit price in USD (converted from its trade currency); null when
  // there's no cost — e.g. rewards, which are free income.
  const entryUnitUsd = (entry: CgEntry): number | null => {
    const fiat = entry.unitPriceFiat ? parseFloat(entry.unitPriceFiat) : 0;

    if (!(fiat > 0)) {
      return null;
    }

    return entry.currency === 'EUR' ? fiat * usdPerEur : fiat;
  };

  // Adapt a ledger entry into the shape the shared edit dialog expects.
  const entryToTx = (entry: CgEntry): CgTransaction => ({
    ...entry,
    walletId: wallet?.id ?? '',
    walletName: wallet?.name ?? '',
    walletType: wallet?.type ?? 'manual',
    totalSpent: null,
    currentPriceUsd: null,
    unitPriceUsd: null,
    unitPriceEurAt: null,
    profitPct: null
  });

  const onRemoveEntry = (entryId: string) => {
    if (wallet) {
      removeEntry.mutate({ walletId: wallet.id, entryId });
    }
  };
  const sync = useSyncCryptoGestWallet();
  const importNexo = useImportNexoCsv();
  const removeWallet = useDeleteCryptoGestWallet();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const removeEntry = useDeleteCryptoGestEntry();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editEntry, setEditEntry] = useState<CgTransaction | null>(null);
  // A held-back CSV import that would orphan entries — awaiting the user's call
  // to delete them and continue, or abort.
  const [orphanPrompt, setOrphanPrompt] = useState<{ csv: string; orphans: NexoOrphan[] } | null>(null);
  const [txSearch, setTxSearch] = useQueryState('q', '');
  // Which types the table shows; `null` means "all" (nothing filtered). Values
  // are entry types for a ledger wallet, or in/out for a network feed.
  const [txType, setTxType] = useState<string[] | null>(null);
  const livePrices = useCryptoGestPriceStream(
    (wallet?.holdings ?? []).map(holding => holding.symbol).filter(Boolean) as string[]
  );

  const onCopyAddress = async () => {
    if (!wallet?.address) {
      return;
    }

    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopied(true);
      success({ message: translate('cryptoGest.wallet.addressCopied') });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  useSetBreadcrumbTrail(wallet?.name ?? null);

  const columns = useMemo<ColumnDef<CgHolding, any>[]>(
    () => [
      {
        accessorKey: 'symbol',
        header: translate('cryptoGest.labels.asset'),
        cell: ({ row }) => (
          <span className={'flex items-center gap-2 font-medium text-foreground'}>
            <AssetIcon logo={row.original.logo} symbol={row.original.symbol} />

            {row.original.symbol}
          </span>
        )
      },
      {
        id: 'amount',
        accessorFn: row => parseFloat(row.amount),
        header: translate('cryptoGest.labels.amount'),
        cell: ({ row }) => amount(row.original.amount),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'price',
        accessorFn: row => row.priceUsd ?? 0,
        header: translate('cryptoGest.labels.price'),
        cell: ({ row }) => usd(row.original.priceUsd ?? 0),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        id: 'value',
        accessorFn: row => row.valueUsd ?? 0,
        header: translate('cryptoGest.labels.value'),
        cell: ({ row }) => usd(row.original.valueUsd ?? 0),
        meta: { align: 'right', className: 'whitespace-nowrap font-medium tabular-nums' }
      },
      // Nexo wallets accrue interest — surface total rewards earned per asset.
      ...(wallet?.type === 'nexo'
        ? [
            {
              id: 'rewards',
              accessorFn: (row: CgHolding) => parseFloat(row.rewardsAmount ?? '0'),
              header: translate('cryptoGest.labels.rewards'),
              cell: ({ row }: { row: { original: CgHolding } }) =>
                row.original.rewardsAmount ? (
                  <div className={'flex flex-col items-end leading-tight'}>
                    <span className={'text-emerald-600'}>
                      {'+'}
                      {amount(row.original.rewardsAmount)}
                    </span>

                    <span className={'text-xs text-muted-foreground'}>
                      {usd(parseFloat(row.original.rewardsAmount) * (row.original.priceUsd ?? 0))}
                    </span>
                  </div>
                ) : (
                  '—'
                ),
              meta: { align: 'right' as const, className: 'whitespace-nowrap tabular-nums' }
            }
          ]
        : [])
    ],
    [translate, usd, wallet?.type]
  );

  const txColumns = useMemo<ColumnDef<CgEntry, any>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        enableSorting: false,
        cell: ({ row }) => <CopyId value={row.original.id} />,
        meta: { className: 'whitespace-nowrap' }
      },
      {
        accessorKey: 'symbol',
        header: translate('cryptoGest.labels.asset'),
        cell: ({ row }) => (
          <span className={'flex items-center gap-2 font-medium text-foreground'}>
            <AssetIcon logo={row.original.logo} symbol={row.original.symbol} />

            {row.original.symbol}
          </span>
        )
      },
      {
        accessorKey: 'type',
        header: translate('cryptoGest.form.entryType'),
        cell: ({ row }) => {
          const isIn = row.original.direction === 'in';

          return (
            <span className={'inline-flex items-center gap-1.5'}>
              <span className={`inline-flex items-center gap-1.5 ${isIn ? 'text-emerald-600' : 'text-red-600'}`}>
                {isIn ? <ArrowDownLeft className={'h-3.5 w-3.5'} /> : <ArrowUpRight className={'h-3.5 w-3.5'} />}

                {translate(`cryptoGest.entryType.${row.original.type}`)}
              </span>

              {row.original.counterSymbol && (
                <span className={'text-xs text-muted-foreground'}>
                  {isIn ? '←' : '→'} {row.original.counterSymbol}
                </span>
              )}

              {row.original.aggregated && (
                <Badge title={translate('cryptoGest.wallet.aggregatedHint')} variant={'secondary'}>
                  {row.original.aggregatedCount
                    ? translate('cryptoGest.wallet.aggregatedCount', { count: row.original.aggregatedCount })
                    : translate('cryptoGest.wallet.aggregated')}
                </Badge>
              )}
            </span>
          );
        }
      },
      {
        id: 'txAmount',
        accessorFn: row => parseFloat(row.amount),
        header: translate('cryptoGest.labels.amount'),
        cell: ({ row }) => {
          const isIn = row.original.direction === 'in';

          return (
            <span className={isIn ? 'text-emerald-600' : 'text-red-600'}>
              {isIn ? '+' : '−'}
              {amount(row.original.amount)}
            </span>
          );
        },
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'unitPrice',
        header: translate('cryptoGest.labels.price'),
        cell: ({ row }) => {
          const unit = entryUnitUsd(row.original);

          return unit != null ? usd(unit) : '—';
        },
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        id: 'value',
        header: translate('cryptoGest.labels.value'),
        cell: ({ row }) => {
          const unit = entryUnitUsd(row.original);

          return unit != null ? usd(parseFloat(row.original.amount) * unit) : '—';
        },
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        accessorKey: 'timestamp',
        header: translate('cryptoGest.form.date'),
        cell: ({ row }) => moment(row.original.timestamp).format('DD MMM YYYY, HH:mm'),
        meta: { className: 'whitespace-nowrap text-muted-foreground' }
      },
      {
        accessorKey: 'txHash',
        header: 'Tx',
        cell: ({ row }) =>
          row.original.txHash ? (
            <span className={'font-mono text-xs text-muted-foreground'}>{shortAddress(row.original.txHash)}</span>
          ) : (
            '—'
          )
      },
      {
        id: 'link',
        header: translate('cryptoGest.wallet.inApp'),
        // Ledger entries ARE the app's transactions, so a manual/nexo row is
        // always linked; MISSING DATA ones still need their purchase value.
        cell: ({ row }) =>
          row.original.needsData ? (
            <Badge className={'border-amber-500/50 text-amber-600'} variant={'outline'}>
              {translate('cryptoGest.transactions.missingData')}
            </Badge>
          ) : (
            <span className={'inline-flex items-center gap-1 text-xs text-emerald-600'}>
              <Check className={'h-3.5 w-3.5'} /> {translate('cryptoGest.wallet.linked')}
            </span>
          ),
        meta: { align: 'right' as const }
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        // Aggregated reward roll-ups are derived from the import — not editable.
        cell: ({ row }) =>
          row.original.aggregated ? null : (
            <div className={'flex items-center justify-end gap-1'}>
              <Button
                className={'h-7 w-7'}
                onClick={() => setEditEntry(entryToTx(row.original))}
                size={'icon'}
                variant={'ghost'}
              >
                <Pencil className={'h-3.5 w-3.5 text-muted-foreground'} />
              </Button>

              <Button
                className={'h-7 w-7'}
                onClick={() => onRemoveEntry(row.original.id)}
                size={'icon'}
                variant={'ghost'}
              >
                <Trash2 className={'h-3.5 w-3.5 text-muted-foreground'} />
              </Button>
            </div>
          ),
        meta: { align: 'right' as const }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, usd, usdPerEur]
  );

  // Collapse consecutive reward payouts of the same asset into one expandable
  // line (a wallet's ledger holds several assets, so keep them apart by symbol).
  const rewardGroup = useMemo<DataTableGroup<CgEntry>>(
    () => ({
      key: entry => (entry.type === 'reward' ? `reward:${entry.symbol}` : null),
      summary: entries => {
        const first = entries[0];
        const totalAmount = entries.reduce((sum, entry) => sum + parseFloat(entry.amount), 0);
        const times = entries.map(entry => new Date(entry.timestamp).getTime());
        const from = moment(Math.min(...times)).format('DD MMM YYYY');
        const to = moment(Math.max(...times)).format('DD MMM YYYY');

        return {
          symbol: (
            <span className={'flex items-center gap-2 font-medium text-foreground'}>
              <AssetIcon logo={first.logo} symbol={first.symbol} />

              {first.symbol}
            </span>
          ),
          type: (
            <span className={'inline-flex items-center gap-1.5 text-emerald-600'}>
              <ArrowDownLeft className={'h-3.5 w-3.5'} />

              {translate('cryptoGest.labels.rewards')}

              <span className={'rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground'}>
                {translate('cryptoGest.group.rewards', { count: entries.length })}
              </span>
            </span>
          ),
          txAmount: <span className={'text-emerald-600'}>{`+${amount(totalAmount)}`}</span>,
          unitPrice: '—',
          value: '—',
          timestamp: (
            <span className={'text-muted-foreground'}>{translate('cryptoGest.group.range', { from, to })}</span>
          )
        };
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  const onCreateFromTx = async (txId: number) => {
    if (!id) {
      return;
    }

    try {
      await createFromTx.mutateAsync({ walletId: id, txId });
      success({ message: translate('cryptoGest.wallet.entryCreated') });
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  const onchainColumns = useMemo<ColumnDef<CgOnchainTx, any>[]>(
    () => [
      {
        accessorKey: 'symbol',
        header: translate('cryptoGest.labels.asset'),
        cell: ({ row }) => (
          <span className={'flex items-center gap-2 font-medium text-foreground'}>
            <AssetIcon logo={row.original.logo} symbol={row.original.symbol} />

            {row.original.symbol}
          </span>
        )
      },
      {
        id: 'direction',
        header: translate('cryptoGest.form.entryType'),
        cell: ({ row }) => {
          const isIn = row.original.direction === 'in';

          return (
            <span className={'inline-flex items-center gap-1.5'}>
              <span className={`inline-flex items-center gap-1.5 ${isIn ? 'text-emerald-600' : 'text-red-600'}`}>
                {isIn ? <ArrowDownLeft className={'h-3.5 w-3.5'} /> : <ArrowUpRight className={'h-3.5 w-3.5'} />}

                {translate(`cryptoGest.entryType.${isIn ? 'transfer_in' : 'transfer_out'}`)}
              </span>

              {!row.original.confirmed && (
                <Badge className={'border-amber-500/40 bg-amber-500/10 text-amber-700'} variant={'outline'}>
                  {translate('cryptoGest.wallet.pending')}
                </Badge>
              )}
            </span>
          );
        }
      },
      {
        id: 'amount',
        accessorFn: row => parseFloat(row.amount),
        header: translate('cryptoGest.labels.amount'),
        cell: ({ row }) => {
          const isIn = row.original.direction === 'in';

          return (
            <span className={isIn ? 'text-emerald-600' : 'text-red-600'}>
              {isIn ? '+' : '−'}
              {amount(row.original.amount)}
            </span>
          );
        },
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums' }
      },
      {
        id: 'unitPrice',
        header: translate('cryptoGest.labels.price'),
        cell: ({ row }) => (row.original.unitPriceFiat != null ? usd(parseFloat(row.original.unitPriceFiat)) : '—'),
        meta: { align: 'right', className: 'whitespace-nowrap tabular-nums text-muted-foreground' }
      },
      {
        accessorKey: 'timestamp',
        header: translate('cryptoGest.form.date'),
        cell: ({ row }) => moment(row.original.timestamp).format('DD MMM YYYY, HH:mm'),
        meta: { className: 'whitespace-nowrap text-muted-foreground' }
      },
      {
        id: 'link',
        header: '',
        cell: ({ row }) =>
          row.original.linkedEntryId ? (
            row.original.linkedNeedsData ? (
              <Badge className={'border-amber-500/50 text-amber-600'} variant={'outline'}>
                {translate('cryptoGest.transactions.missingData')}
              </Badge>
            ) : (
              <span className={'inline-flex items-center gap-1 text-xs text-emerald-600'}>
                <Check className={'h-3.5 w-3.5'} /> {translate('cryptoGest.wallet.linked')}
              </span>
            )
          ) : (
            <Button
              disabled={createFromTx.isLoading}
              onClick={() => onCreateFromTx(row.original.id)}
              size={'sm'}
              variant={'outline'}
            >
              {translate('cryptoGest.wallet.createEntry')}
            </Button>
          ),
        meta: { align: 'right' }
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          // Feed rows edit/delete their linked ledger entry (the app transaction).
          const linked = row.original.linkedEntryId
            ? (entries ?? []).find(entry => entry.id === row.original.linkedEntryId)
            : undefined;

          if (!linked) {
            return null;
          }

          return (
            <div className={'flex items-center justify-end gap-1'}>
              <Button
                className={'h-7 w-7'}
                onClick={() => setEditEntry(entryToTx(linked))}
                size={'icon'}
                variant={'ghost'}
              >
                <Pencil className={'h-3.5 w-3.5 text-muted-foreground'} />
              </Button>

              <Button className={'h-7 w-7'} onClick={() => onRemoveEntry(linked.id)} size={'icon'} variant={'ghost'}>
                <Trash2 className={'h-3.5 w-3.5 text-muted-foreground'} />
              </Button>
            </div>
          );
        },
        meta: { align: 'right' }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, usd, createFromTx.isLoading, entries]
  );

  if (isLoading || !wallet) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  const onSync = async () => {
    try {
      await sync.mutateAsync(wallet.id);
      success({ message: translate('cryptoGest.wallet.syncStarted') });
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  // Run an import. When the export would orphan entries the backend applies
  // nothing and returns them — hold the CSV and surface the warning instead.
  const runImport = async (csv: string, deleteOrphans?: boolean) => {
    const result = await importNexo.mutateAsync({ id: wallet.id, csv, deleteOrphans });

    if (result.status === 'orphans') {
      setOrphanPrompt({ csv, orphans: result.orphans ?? [] });

      return;
    }

    setOrphanPrompt(null);

    success({
      message: translate('cryptoGest.wallet.nexoImported', {
        added: result.added,
        updated: result.updated,
        deleted: result.deleted,
        rewards: result.rewards,
        skipped: result.skipped
      })
    });
  };

  const onImportNexo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // Reset the input so re-selecting the same file fires change again.
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      await runImport(await file.text());
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  const onConfirmDeleteOrphans = async () => {
    if (!orphanPrompt) {
      return;
    }

    try {
      await runImport(orphanPrompt.csv, true);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  const onRemove = async () => {
    try {
      await removeWallet.mutateAsync(wallet.id);
      success({ message: translate('cryptoGest.wallet.deleted') });
      navigate(appRoutes.dashboard.cryptoGest.wallets);
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  const baseHoldings = wallet.holdings ?? [];

  // Overlay live prices (WebSocket) on top of the static snapshot so price and
  // value update in near-real-time without a refetch.
  const holdings = baseHoldings.map(holding => {
    const live = holding.symbol ? livePrices[holding.symbol.toUpperCase()] : undefined;

    if (live === undefined) {
      return holding;
    }

    return {
      ...holding,
      priceUsd: live,
      valueUsd: parseFloat(holding.amount || '0') * live
    };
  });

  // Transaction filters (search + type), applied to whichever table is shown.
  const isLedger = wallet.type === 'manual' || wallet.type === 'nexo';
  const txQuery = txSearch.trim().toLowerCase();

  const matchesSearch = (row: {
    symbol?: string | null;
    amount?: string;
    note?: string | null;
    txHash?: string | null;
    timestamp?: string;
  }) => {
    if (!txQuery) {
      return true;
    }

    return [
      row.symbol,
      row.amount,
      row.note,
      row.txHash,
      row.timestamp && moment(row.timestamp).format('DD MMM YYYY, HH:mm')
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(txQuery);
  };

  // Type options depend on the table: entry types for the ledger, direction for
  // the network feed. Only the values actually present are offered.
  const typeOptions = isLedger
    ? ENTRY_TYPE_ORDER.filter(type => (entries ?? []).some(entry => entry.type === type)).map(type => ({
        value: type,
        label: translate(`cryptoGest.entryType.${type}`)
      }))
    : [
        { value: 'in', label: translate('cryptoGest.transactions.directionIn') },
        { value: 'out', label: translate('cryptoGest.transactions.directionOut') }
      ];
  const selectedTypes = txType ?? typeOptions.map(option => option.value);
  const selectedTypeSet = new Set(selectedTypes);

  const ledgerRows = (entries ?? []).filter(entry => selectedTypeSet.has(entry.type) && matchesSearch(entry));
  const feedRows = (onchainTx ?? []).filter(tx => selectedTypeSet.has(tx.direction) && matchesSearch(tx));

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-center justify-between gap-3'}>
        <div>
          <Title>{wallet.name}</Title>

          <div className={'mt-1 flex items-center gap-2 text-sm text-muted-foreground'}>
            <Badge variant={'secondary'}>{translate(`cryptoGest.type.${wallet.type}`)}</Badge>

            {wallet.type === 'exchange' && (
              <span className={'text-xs capitalize'}>{wallet.provider ?? translate('cryptoGest.type.exchange')}</span>
            )}

            {wallet.type === 'tracked' && (
              <span className={'flex items-center gap-1.5 font-mono text-xs'}>
                {wallet.chainLabel}
                {' · '}

                <button
                  aria-label={translate('cryptoGest.wallet.copyAddress')}
                  className={'inline-flex items-center gap-1 rounded transition-colors hover:text-foreground'}
                  onClick={onCopyAddress}
                  title={wallet.address ?? ''}
                  type={'button'}
                >
                  {shortAddress(wallet.address)}

                  {copied ? (
                    <Check className={'h-3 w-3 text-emerald-600'} />
                  ) : (
                    <Copy className={'h-3 w-3 opacity-60'} />
                  )}
                </button>
              </span>
            )}
          </div>
        </div>

        <div className={'flex items-center gap-2'}>
          <Button onClick={() => navigate(appRoutes.dashboard.cryptoGest.transactions)} variant={'outline'}>
            <ArrowLeftRight className={'h-4 w-4'} />

            {translate('cryptoGest.menu.transactions')}
          </Button>

          {(wallet.type === 'tracked' || wallet.type === 'exchange') && (
            <Button disabled={sync.isLoading} onClick={onSync} variant={'outline'}>
              <RefreshCw className={`h-4 w-4 ${sync.isLoading ? 'animate-spin' : ''}`} />

              {translate('cryptoGest.wallet.syncNow')}
            </Button>
          )}

          {wallet.type === 'nexo' && (
            <>
              <input
                accept={'.csv,text/csv'}
                className={'hidden'}
                onChange={onImportNexo}
                ref={fileInputRef}
                type={'file'}
              />

              <Button disabled={importNexo.isLoading} onClick={() => fileInputRef.current?.click()} variant={'outline'}>
                <Upload className={`h-4 w-4 ${importNexo.isLoading ? 'animate-pulse' : ''}`} />

                {translate('cryptoGest.wallet.importCsv')}
              </Button>
            </>
          )}

          <Button
            aria-label={translate('cryptoGest.wallet.edit')}
            onClick={() => setEditing(true)}
            size={'icon'}
            title={translate('cryptoGest.wallet.edit')}
            variant={'ghost'}
          >
            <Pencil className={'h-4 w-4'} />
          </Button>

          <Button onClick={() => setConfirmRemove(true)} size={'icon'} variant={'ghost'}>
            <Trash2 className={'h-4 w-4 text-destructive'} />
          </Button>
        </div>
      </div>

      {wallet.type !== 'manual' && wallet.syncError && (
        <div className={'rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive'}>
          {wallet.syncError}
        </div>
      )}

      {/* Holdings + transactions, switchable via tabs. */}
      <Tabs className={'w-full'} defaultValue={'holdings'}>
        <TabsList>
          <TabsTrigger value={'holdings'}>{translate('cryptoGest.wallet.holdings')}</TabsTrigger>

          <TabsTrigger value={'transactions'}>
            {isLedger ? translate('cryptoGest.wallet.entries') : translate('cryptoGest.wallet.networkTransactions')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={'holdings'}>
          <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
            <DataTable
              columns={columns}
              data={holdings}
              emptyMessage={translate('cryptoGest.wallet.noHoldings')}
              initialSorting={[{ id: 'value', desc: true }]}
              onRowClick={holding => navigate(`/dashboard/crypto-gest/assets/${holding.assetId}?wallet=${wallet.id}`)}
            />
          </div>
        </TabsContent>

        {/* The local ledger for manual/nexo wallets, the raw network feed
            (linkable to local transactions) for tracked/exchange wallets. */}
        <TabsContent value={'transactions'}>
          <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
            <div className={'flex flex-wrap items-center justify-end gap-2 px-4 py-3'}>
              <div className={'relative min-w-[180px]'}>
                <Search
                  className={
                    'pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground'
                  }
                />

                <Input
                  className={'h-9 pl-8'}
                  onChange={event => setTxSearch(event.target.value)}
                  placeholder={translate('cryptoGest.transactions.searchPlaceholder')}
                  value={txSearch}
                />
              </div>

              <div className={'w-48'}>
                <MultiSelectFilter
                  allLabel={translate('cryptoGest.filter.all')}
                  clearLabel={translate('cryptoGest.filter.clear')}
                  label={translate('cryptoGest.filter.types')}
                  noneLabel={translate('cryptoGest.filter.none')}
                  onChange={setTxType}
                  options={typeOptions}
                  selectAllLabel={translate('cryptoGest.filter.selectAll')}
                  summary={(count, total) => translate('cryptoGest.filter.count', { count, total })}
                  value={selectedTypes}
                />
              </div>
            </div>

            {isLedger ? (
              <DataTable
                columns={txColumns}
                data={ledgerRows}
                emptyMessage={translate('cryptoGest.wallet.noEntries')}
                group={rewardGroup}
                onRowClick={entry => navigate(`/dashboard/crypto-gest/assets/${entry.assetId}?wallet=${wallet.id}`)}
                pageSize={25}
              />
            ) : (
              <DataTable
                columns={onchainColumns}
                data={feedRows}
                emptyMessage={translate('cryptoGest.wallet.noEntries')}
                pageSize={25}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit / fill a transaction — same dialog as the Transactions page */}
      <EditTransactionDialog entry={editEntry} onOpenChange={open => !open && setEditEntry(null)} />

      {/* Edit wallet */}
      <EditWalletDialog onOpenChange={setEditing} open={editing} wallet={wallet} />

      {/* Remove confirmation */}
      <Dialog onOpenChange={setConfirmRemove} open={confirmRemove}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('cryptoGest.wallet.removeTitle')}</DialogTitle>
          </DialogHeader>

          <p className={'text-sm text-muted-foreground'}>{translate('cryptoGest.wallet.removeDescription')}</p>

          <div className={'mt-4 flex justify-end gap-2'}>
            <Button onClick={() => setConfirmRemove(false)} variant={'outline'}>
              {translate('common.actions.cancel')}
            </Button>

            <Button disabled={removeWallet.isLoading} onClick={onRemove} variant={'destructive'}>
              {translate('cryptoGest.wallet.remove')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Orphaned entries on re-import — delete them and continue, or abort */}
      <Dialog onOpenChange={open => !open && setOrphanPrompt(null)} open={!!orphanPrompt}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('cryptoGest.wallet.orphanTitle')}</DialogTitle>
          </DialogHeader>

          <p className={'text-sm text-muted-foreground'}>{translate('cryptoGest.wallet.orphanDescription')}</p>

          <div className={'mt-2 max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border'}>
            {(orphanPrompt?.orphans ?? []).map(orphan => (
              <div className={'flex items-center justify-between gap-3 px-3 py-2 text-sm'} key={orphan.id}>
                <span className={'flex items-center gap-2'}>
                  <span className={'font-medium text-foreground'}>{orphan.symbol}</span>

                  <span className={'text-muted-foreground'}>{translate(`cryptoGest.entryType.${orphan.type}`)}</span>
                </span>

                <span className={'flex items-center gap-3 tabular-nums'}>
                  <span>{amount(orphan.amount)}</span>

                  <span className={'text-xs text-muted-foreground'}>
                    {moment(orphan.timestamp).format('DD MMM YYYY')}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div className={'mt-4 flex justify-end gap-2'}>
            <Button onClick={() => setOrphanPrompt(null)} variant={'outline'}>
              {translate('cryptoGest.wallet.orphanAbort')}
            </Button>

            <Button disabled={importNexo.isLoading} onClick={onConfirmDeleteOrphans} variant={'destructive'}>
              {translate('cryptoGest.wallet.orphanDelete', { count: orphanPrompt?.orphans.length ?? 0 })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Dialog to edit a wallet's editable fields (name). Other fields (type, chain,
 * address) are immutable — re-add the wallet to change them.
 */

function EditWalletDialog({
  wallet,
  open,
  onOpenChange
}: {
  wallet: CgWallet;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const edit = useEditCryptoGestWallet();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({ mode: 'all', defaultValues: { name: wallet.name } });

  useEffect(() => {
    if (open) {
      reset({ name: wallet.name });
    }
  }, [open, wallet, reset]);

  const onSubmit = async (values: any) => {
    try {
      await edit.mutateAsync({ id: wallet.id, name: values.name });

      success({ message: translate('cryptoGest.wallet.updated') });
      onOpenChange(false);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('cryptoGest.wallet.edit')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <InputField
            control={control}
            label={translate('cryptoGest.form.name')}
            name={'name'}
            placeholder={translate('cryptoGest.form.namePlaceholder')}
            rules={{ required: translate('common.errors.form.required') }}
          />

          <div className={'mt-4 flex justify-end gap-2'}>
            <Button onClick={() => onOpenChange(false)} type={'button'} variant={'outline'}>
              {translate('common.actions.cancel')}
            </Button>

            <SubmitButton
              className={'mt-0'}
              disabled={isSubmitting}
              label={translate('common.actions.save')}
              loading={edit.isLoading}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
